import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, Download, Send, X, Receipt, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { v: "draft", label: "Brouillon", color: "bg-gray-100 text-gray-700" },
  { v: "sent", label: "Envoyée", color: "bg-blue-100 text-blue-700" },
  { v: "paid", label: "Payée", color: "bg-green-100 text-green-700" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const plusDays = (d) => new Date(Date.now() + d * 86400000).toISOString().slice(0, 10);

const emptyForm = () => ({
  client: { name: "", company: "", email: "", phone: "", address: "" },
  project: "",
  issue_date: todayISO(),
  due_date: plusDays(30),
  items: [{ description: "", quantity: 1, unit_price: 0 }],
  gst_rate: 5.0,
  qst_rate: 9.975,
  notes: "Acompte 50 % à la signature, solde à la livraison. Paiement par virement Interac à contact@passerellenumerique.ca.",
  status: "draft",
});

export default function InvoicesPanel() {
  const [invoices, setInvoices] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | id
  const [form, setForm] = useState(emptyForm());
  const [sending, setSending] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/invoices");
      setInvoices(data);
    } catch { toast.error("Erreur de chargement"); }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyForm()); setEditing("new"); };
  const openEdit = (inv) => { setForm({ ...inv }); setEditing(inv.id); };
  const close = () => { setEditing(null); setForm(emptyForm()); };

  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { description: "", quantity: 1, unit_price: 0 }] }));
  const updateItem = (i, k, v) => setForm((f) => {
    const items = [...f.items];
    items[i] = { ...items[i], [k]: k === "description" ? v : Number(v) || 0 };
    return { ...f, items };
  });
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const totals = (() => {
    const sub = form.items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
    const gst = +(sub * (Number(form.gst_rate) || 0) / 100).toFixed(2);
    const qst = +(sub * (Number(form.qst_rate) || 0) / 100).toFixed(2);
    return { sub, gst, qst, total: +(sub + gst + qst).toFixed(2) };
  })();

  const save = async () => {
    if (!form.client.name || form.items.length === 0) {
      toast.error("Nom du client et au moins un item requis."); return;
    }
    try {
      if (editing === "new") {
        await api.post("/admin/invoices", form);
        toast.success("Facture créée");
      } else {
        await api.put(`/admin/invoices/${editing}`, form);
        toast.success("Facture mise à jour");
      }
      close(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette facture ?")) return;
    await api.delete(`/admin/invoices/${id}`);
    toast.success("Supprimée");
    load();
  };

  const downloadPdf = async (inv) => {
    try {
      const res = await api.get(`/admin/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Erreur téléchargement PDF"); }
  };

  const sendEmail = async (inv) => {
    if (!inv.client?.email) { toast.error("Aucune adresse courriel client."); return; }
    if (!window.confirm(`Envoyer la facture ${inv.number} à ${inv.client.email} ?`)) return;
    setSending(inv.id);
    try {
      await api.post(`/admin/invoices/${inv.id}/send`);
      toast.success("Facture envoyée par courriel");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur envoi"); }
    finally { setSending(null); }
  };

  const stats = {
    total: invoices.length,
    revenue: invoices.filter((i) => i.status === "paid").reduce((s, i) => s + (i.total || 0), 0),
    pending: invoices.filter((i) => i.status === "sent").reduce((s, i) => s + (i.total || 0), 0),
  };

  return (
    <div data-testid="invoices-panel">
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <Receipt className="h-5 w-5 text-[#00B8D9]" />
          <div className="text-3xl font-heading font-extrabold text-[#0B1F3A] mt-2">{stats.total}</div>
          <div className="text-sm text-[#4B5563]">Factures totales</div>
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <DollarSign className="h-5 w-5 text-green-500" />
          <div className="text-3xl font-heading font-extrabold text-[#0B1F3A] mt-2">{stats.revenue.toLocaleString("fr-CA", { maximumFractionDigits: 0 })} $</div>
          <div className="text-sm text-[#4B5563]">Revenus encaissés</div>
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <DollarSign className="h-5 w-5 text-orange-500" />
          <div className="text-3xl font-heading font-extrabold text-[#0B1F3A] mt-2">{stats.pending.toLocaleString("fr-CA", { maximumFractionDigits: 0 })} $</div>
          <div className="text-sm text-[#4B5563]">En attente</div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-bold text-xl text-[#0B1F3A]">Factures</h2>
        <Button onClick={openNew} className="bg-[#00B8D9] hover:bg-[#0096B3]" data-testid="invoice-new"><Plus className="h-4 w-4 mr-1" />Nouvelle facture</Button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F7FA] text-left">
            <tr>
              <th className="p-3">N°</th><th className="p-3">Client</th><th className="p-3">Émise</th>
              <th className="p-3">Échéance</th><th className="p-3">Total</th><th className="p-3">Statut</th><th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-[#4B5563]">Aucune facture. Créez-en une pour commencer.</td></tr>
            )}
            {invoices.map((inv) => {
              const st = STATUS_OPTIONS.find((s) => s.v === inv.status) || STATUS_OPTIONS[0];
              return (
                <tr key={inv.id} className="border-t border-gray-100" data-testid={`invoice-row-${inv.number}`}>
                  <td className="p-3 font-mono font-bold text-[#0B1F3A]">{inv.number}</td>
                  <td className="p-3">
                    <div className="font-medium text-[#0B1F3A]">{inv.client?.name}</div>
                    <div className="text-xs text-[#4B5563]">{inv.client?.company}</div>
                  </td>
                  <td className="p-3 text-[#4B5563]">{inv.issue_date}</td>
                  <td className="p-3 text-[#4B5563]">{inv.due_date}</td>
                  <td className="p-3 font-bold text-[#0B1F3A]">{(inv.total || 0).toLocaleString("fr-CA", { minimumFractionDigits: 2 })} $</td>
                  <td className="p-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${st.color}`}>{st.label}</span></td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => downloadPdf(inv)} title="Télécharger PDF" data-testid={`invoice-pdf-${inv.number}`}><Download className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => sendEmail(inv)} disabled={sending === inv.id} title="Envoyer par courriel" data-testid={`invoice-send-${inv.number}`}><Send className="h-4 w-4 text-[#00B8D9]" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(inv)} title="Éditer"><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(inv.id)} title="Supprimer"><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && close()}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editing === "new" ? "Nouvelle facture" : `Éditer la facture`}</DialogTitle></DialogHeader>

          <div className="space-y-5">
            <div>
              <div className="text-xs tracking-[0.18em] uppercase font-bold text-[#00B8D9] mb-2">Client</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Nom *</Label><Input value={form.client.name} onChange={(e) => setForm({ ...form, client: { ...form.client, name: e.target.value } })} data-testid="inv-client-name" /></div>
                <div><Label>Entreprise</Label><Input value={form.client.company} onChange={(e) => setForm({ ...form, client: { ...form.client, company: e.target.value } })} /></div>
                <div><Label>Courriel</Label><Input type="email" value={form.client.email} onChange={(e) => setForm({ ...form, client: { ...form.client, email: e.target.value } })} data-testid="inv-client-email" /></div>
                <div><Label>Téléphone</Label><Input value={form.client.phone} onChange={(e) => setForm({ ...form, client: { ...form.client, phone: e.target.value } })} /></div>
              </div>
              <div className="mt-3"><Label>Adresse</Label><Input value={form.client.address} onChange={(e) => setForm({ ...form, client: { ...form.client, address: e.target.value } })} /></div>
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              <div className="sm:col-span-3"><Label>Projet / Référence</Label><Input value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })} placeholder="Ex: Refonte site web + SEO local" /></div>
              <div><Label>Date d'émission</Label><Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} /></div>
              <div><Label>Échéance</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
              <div>
                <Label>Statut</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs tracking-[0.18em] uppercase font-bold text-[#00B8D9]">Items</div>
                <Button size="sm" variant="outline" onClick={addItem} data-testid="inv-add-item"><Plus className="h-3 w-3 mr-1" />Ajouter</Button>
              </div>
              <div className="space-y-2">
                {form.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end p-3 bg-[#F5F7FA] rounded-lg">
                    <div className="col-span-6"><Label className="text-xs">Description</Label><Input value={it.description} onChange={(e) => updateItem(i, "description", e.target.value)} /></div>
                    <div className="col-span-2"><Label className="text-xs">Qté</Label><Input type="number" step="0.01" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} /></div>
                    <div className="col-span-3"><Label className="text-xs">Prix unitaire ($)</Label><Input type="number" step="0.01" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} /></div>
                    <div className="col-span-1 text-right">
                      <Button size="sm" variant="ghost" onClick={() => removeItem(i)} disabled={form.items.length === 1}><X className="h-4 w-4 text-red-500" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div><Label>TPS (%)</Label><Input type="number" step="0.001" value={form.gst_rate} onChange={(e) => setForm({ ...form, gst_rate: Number(e.target.value) })} /></div>
                <div><Label>TVQ (%)</Label><Input type="number" step="0.001" value={form.qst_rate} onChange={(e) => setForm({ ...form, qst_rate: Number(e.target.value) })} /></div>
                <div><Label>Notes / Conditions</Label><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              </div>
              <div className="bg-[#0B1F3A] text-white rounded-xl p-5 space-y-2 self-start">
                <div className="text-xs tracking-[0.18em] uppercase font-bold text-[#00B8D9]">Récapitulatif</div>
                <div className="flex justify-between text-sm pt-2"><span>Sous-total</span><span>{totals.sub.toLocaleString("fr-CA", { minimumFractionDigits: 2 })} $</span></div>
                <div className="flex justify-between text-sm"><span>TPS ({form.gst_rate}%)</span><span>{totals.gst.toLocaleString("fr-CA", { minimumFractionDigits: 2 })} $</span></div>
                <div className="flex justify-between text-sm"><span>TVQ ({form.qst_rate}%)</span><span>{totals.qst.toLocaleString("fr-CA", { minimumFractionDigits: 2 })} $</span></div>
                <div className="flex justify-between font-heading font-extrabold text-2xl pt-3 border-t border-white/20 mt-3 text-[#00B8D9]">
                  <span>TOTAL</span><span>{totals.total.toLocaleString("fr-CA", { minimumFractionDigits: 2 })} $</span>
                </div>
                <div className="text-[10px] text-gray-300 mt-1">CAD</div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
              <Button variant="outline" onClick={close}>Annuler</Button>
              <Button onClick={save} className="bg-[#00B8D9] hover:bg-[#0096B3]" data-testid="inv-save">Enregistrer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
